import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, Users, ShoppingBag, Euro, Download, Plus, 
  CheckCircle, Clock, Eye, LogOut, FileText, Pencil, Trash2, Image, Save, X
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingAccessory, setEditingAccessory] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    email: '', nom: '', prenom: '', telephone: '',
    adresse: '', code_postal: '', ville: '', taille_cycliste: '',
    order_details: '', notes: ''
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [accessoryDialogOpen, setAccessoryDialogOpen] = useState(false);
  const [newAccessoryDialogOpen, setNewAccessoryDialogOpen] = useState(false);
  const [newAccessory, setNewAccessory] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    price: '',
    image: ''
  });
  const [partnerStores, setPartnerStores] = useState([]);
  const [editingStore, setEditingStore] = useState(null);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [newStore, setNewStore] = useState({
    name: '', address: '', city: '', phone: '', website: ''
  });
  // Config options state
  const [configOptions, setConfigOptions] = useState({});
  const [baseColors, setBaseColors] = useState([]);
  const [ralColors, setRalColors] = useState([]);
  const [editingOption, setEditingOption] = useState(null);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [colorType, setColorType] = useState('base'); // 'base' or 'ral'
  // Bank details state
  const [bankDetails, setBankDetails] = useState({ iban: '', account_name: '', rib_url: '' });
  const [aboutContent, setAboutContent] = useState({ 
    video_url: '', 
    video_type: 'youtube',
    video_title_fr: 'Découvrez Belleville Machine',
    video_title_en: 'Discover Belleville Machine'
  });
  const [homeContent, setHomeContent] = useState({
    hero_subtitle_fr: 'Vélos cargo français',
    hero_subtitle_en: 'French cargo bikes',
    hero_title1_fr: 'BELLEVILLE',
    hero_title1_en: 'BELLEVILLE',
    hero_title2_fr: 'MACHINE',
    hero_title2_en: 'MACHINE',
    hero_description_fr: 'Des vélos cargo conçus et assemblés en France, pensés pour la ville et ses environs.',
    hero_description_en: 'Cargo bikes designed and assembled in France, made for the city and its surroundings.',
    hero_cta_fr: 'Découvrir',
    hero_cta_en: 'Discover',
    hero_cta_contact_fr: 'Nous contacter',
    hero_cta_contact_en: 'Contact us',
    products_subtitle_fr: 'Notre gamme',
    products_subtitle_en: 'Our range',
    products_title_fr: 'Nos vélos',
    products_title_en: 'Our bikes',
    hero_image_url: ''
  });
  const [faqItems, setFaqItems] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, ordersRes, customersRes, productsRes, storesRes, optionsRes, colorsRes, bankRes, aboutRes, faqRes, homeRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/partner-stores`, { headers }),
        axios.get(`${API}/admin/config-options`, { headers }),
        axios.get(`${API}/admin/colors`, { headers }),
        axios.get(`${API}/admin/bank-details`, { headers }),
        axios.get(`${API}/admin/about-content`, { headers }),
        axios.get(`${API}/admin/faq`, { headers }),
        axios.get(`${API}/admin/home-content`, { headers })
      ]);

      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setPartnerStores(storesRes.data || []);
      setProducts(productsRes.data.products || []);
      setAccessories(productsRes.data.accessories || []);
      setConfigOptions(optionsRes.data.options || {});
      setBaseColors(colorsRes.data.base_colors || []);
      setRalColors(colorsRes.data.ral_colors || []);
      setBankDetails(bankRes.data || { iban: '', account_name: '', rib_url: '' });
      setAboutContent(aboutRes.data || { video_url: '', video_type: 'youtube' });
      setFaqItems(faqRes.data.items || []);
      setHomeContent(homeRes.data || homeContent);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      // Error handled silently
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const newToken = response.data.token;
      localStorage.setItem('admin_token', newToken);
      setToken(newToken);
      setIsLoggedIn(true);
      toast.success('Connexion réussie');
    } catch (error) {
      toast.error('Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsLoggedIn(false);
    setStats(null);
    setOrders([]);
    setCustomers([]);
    navigate('/');
  };

  const handleSetupAdmin = async () => {
    try {
      // Use bootstrap endpoint for initial admin creation (only works if no admin exists)
      // Credentials are set server-side for security
      await axios.post(`${API}/admin/bootstrap`, { 
        username: process.env.REACT_APP_ADMIN_USERNAME || 'admin', 
        password: process.env.REACT_APP_ADMIN_PASSWORD || 'changeme',
        email: process.env.REACT_APP_ADMIN_EMAIL || ''
      });
      toast.success('Compte admin créé. Consultez vos variables d\'environnement pour les identifiants.');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Un compte admin existe déjà. Connectez-vous.');
      } else {
        toast.error('Erreur lors de la création du compte admin');
      }
    }
  };

  const handleRecoverPassword = async () => {
    const email = prompt('Entrez votre email admin:');
    if (!email) return;
    
    try {
      const response = await axios.post(`${API}/admin/recover-password`, { email });
      if (response.data.hint) {
        toast.success(response.data.hint);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la récupération');
    }
  };

  const handleValidateTransfer = async (orderId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/orders/${orderId}/validate-transfer`, {}, { headers });
      toast.success('Virement validé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder({
      ...order,
      customer: { ...order.customer }
    });
    setOrderDialogOpen(true);
  };

  const handleSaveOrder = async () => {
    if (!editingOrder) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/orders/${editingOrder.id}`, {
        customer: editingOrder.customer,
        items: editingOrder.items,
        total_amount: parseFloat(editingOrder.total_amount),
        amount_due: parseFloat(editingOrder.amount_due),
        status: editingOrder.status,
        payment_status: editingOrder.payment_status,
        notes: editingOrder.notes || ''
      }, { headers });
      toast.success('Commande mise à jour');
      setOrderDialogOpen(false);
      setEditingOrder(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers });
      toast.success('Commande supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExportExcel = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/export/customers`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_belleville.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/customers`, {
        customer: {
          email: newCustomer.email,
          nom: newCustomer.nom,
          prenom: newCustomer.prenom,
          telephone: newCustomer.telephone,
          adresse: newCustomer.adresse,
          code_postal: newCustomer.code_postal,
          ville: newCustomer.ville,
          taille_cycliste: newCustomer.taille_cycliste
        },
        order_details: newCustomer.order_details,
        notes: newCustomer.notes
      }, { headers });
      
      toast.success('Client créé');
      setDialogOpen(false);
      setNewCustomer({
        email: '', nom: '', prenom: '', telephone: '',
        adresse: '', code_postal: '', ville: '', taille_cycliste: '',
        order_details: '', notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleSaveProduct = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/products/${editingProduct.id}`, {
        name: editingProduct.name,
        description: editingProduct.description,
        description_en: editingProduct.description_en,
        base_price: parseFloat(editingProduct.base_price),
        images: editingProduct.images,
        type: editingProduct.type,
        specs: editingProduct.specs || {},
        promo_title: editingProduct.promo_title || '',
        promo_title_en: editingProduct.promo_title_en || '',
        promo_text: editingProduct.promo_text || '',
        promo_text_en: editingProduct.promo_text_en || '',
        promo_price: editingProduct.promo_price ? parseFloat(editingProduct.promo_price) : null,
        promo_end_date: editingProduct.promo_end_date || null,
        promo_active: editingProduct.promo_active || false,
        active: editingProduct.active !== false
      }, { headers });
      
      toast.success('Produit mis à jour');
      setProductDialogOpen(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Specs management functions
  const handleAddSpec = () => {
    if (!editingProduct) return;
    const newKey = `spec_${Date.now()}`;
    setEditingProduct({
      ...editingProduct,
      specs: {
        ...(editingProduct.specs || {}),
        [newKey]: { label: '', label_en: '', value: '' }
      }
    });
  };

  const handleUpdateSpec = (key, field, value) => {
    if (!editingProduct) return;
    setEditingProduct({
      ...editingProduct,
      specs: {
        ...(editingProduct.specs || {}),
        [key]: {
          ...(editingProduct.specs?.[key] || {}),
          [field]: value
        }
      }
    });
  };

  const handleDeleteSpec = (key) => {
    if (!editingProduct) return;
    const newSpecs = { ...(editingProduct.specs || {}) };
    delete newSpecs[key];
    setEditingProduct({
      ...editingProduct,
      specs: newSpecs
    });
  };

  const handleSaveAccessory = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/accessories/${editingAccessory.id}`, {
        name: editingAccessory.name,
        name_en: editingAccessory.name_en,
        description: editingAccessory.description,
        description_en: editingAccessory.description_en,
        price: parseFloat(editingAccessory.price),
        image: editingAccessory.images?.[0] || ''
      }, { headers });
      
      toast.success('Accessoire mis à jour');
      setAccessoryDialogOpen(false);
      setEditingAccessory(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreateAccessory = async () => {
    if (!newAccessory.name || !newAccessory.price) {
      toast.error('Nom et prix requis');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/accessories`, {
        name: newAccessory.name,
        name_en: newAccessory.name_en || newAccessory.name,
        description: newAccessory.description,
        description_en: newAccessory.description_en || newAccessory.description,
        price: parseFloat(newAccessory.price),
        image: newAccessory.image
      }, { headers });
      
      toast.success('Accessoire créé');
      setNewAccessoryDialogOpen(false);
      setNewAccessory({
        name: '',
        name_en: '',
        description: '',
        description_en: '',
        price: '',
        image: ''
      });
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail?.includes('Maximum')) {
        toast.error('Maximum 10 accessoires autorisés');
      } else {
        toast.error('Erreur lors de la création');
      }
    }
  };

  const handleDeleteAccessory = async (accessoryId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet accessoire ?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/accessories/${accessoryId}`, { headers });
      toast.success('Accessoire supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUploadNewAccessoryImage = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNewAccessory({ ...newAccessory, image: response.data.url });
      toast.success('Photo ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  // Partner Stores functions
  const handleCreateStore = async () => {
    if (!newStore.name || !newStore.city) {
      toast.error('Nom et ville requis');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/partner-stores`, newStore, { headers });
      toast.success('Magasin ajouté');
      setStoreDialogOpen(false);
      setNewStore({ name: '', address: '', city: '', phone: '', website: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStore = async () => {
    if (!editingStore) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/partner-stores/${editingStore.id}`, {
        name: editingStore.name,
        address: editingStore.address,
        city: editingStore.city,
        phone: editingStore.phone,
        website: editingStore.website
      }, { headers });
      toast.success('Magasin mis à jour');
      setEditingStore(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteStore = async (storeId) => {
    if (!confirm('Supprimer ce magasin partenaire ?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/partner-stores/${storeId}`, { headers });
      toast.success('Magasin supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==================== CONFIG OPTIONS FUNCTIONS ====================
  
  const OPTION_CATEGORY_LABELS = {
    types: { fr: 'Type de vélo', en: 'Bike Type' },
    plateaux: { fr: 'Plateaux', en: 'Chainrings' },
    vitesses: { fr: 'Vitesses', en: 'Speeds' },
    guidons: { fr: 'Guidon', en: 'Handlebar' },
    potences: { fr: 'Potence', en: 'Stem' },
    tailles: { fr: 'Taille', en: 'Size' },
    axes: { fr: 'Type d\'axe', en: 'Axle Type' }
  };

  const handleEditOption = (category, item, index) => {
    setEditingOption({
      category,
      index,
      item: { ...item }
    });
    setOptionDialogOpen(true);
  };

  const handleAddOption = (category) => {
    setEditingOption({
      category,
      index: -1, // -1 means new
      item: {
        value: '',
        label: '',
        label_en: '',
        description: '',
        description_en: '',
        price: null
      }
    });
    setOptionDialogOpen(true);
  };

  const handleSaveOption = async () => {
    if (!editingOption) return;
    const { category, index, item } = editingOption;
    
    if (!item.value || !item.label) {
      toast.error('Valeur et nom requis');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentItems = [...(configOptions[category] || [])];
      
      if (index === -1) {
        // Add new
        currentItems.push(item);
      } else {
        // Update existing
        currentItems[index] = item;
      }
      
      await axios.put(`${API}/admin/config-options/${category}`, {
        category,
        items: currentItems
      }, { headers });
      
      toast.success('Option sauvegardée');
      setOptionDialogOpen(false);
      setEditingOption(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteOption = async (category, index) => {
    if (!confirm('Supprimer cette option ?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentItems = [...(configOptions[category] || [])];
      currentItems.splice(index, 1);
      
      await axios.put(`${API}/admin/config-options/${category}`, {
        category,
        items: currentItems
      }, { headers });
      
      toast.success('Option supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==================== BANK DETAILS FUNCTIONS ====================

  const handleSaveBankDetails = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/bank-details`, bankDetails, { headers });
      toast.success('Coordonnées bancaires enregistrées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // ==================== ABOUT PAGE FUNCTIONS ====================

  const handleSaveAboutContent = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/about-content`, aboutContent, { headers });
      toast.success('Contenu de la page À propos enregistré');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // ==================== HOME PAGE FUNCTIONS ====================

  const handleSaveHomeContent = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/home-content`, homeContent, { headers });
      toast.success('Contenu de la page d\'accueil enregistré');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // ==================== FAQ FUNCTIONS ====================

  const handleAddFaq = () => {
    setEditingFaq({
      id: null,
      question_fr: '',
      question_en: '',
      answer_fr: '',
      answer_en: ''
    });
  };

  const handleEditFaq = (item) => {
    setEditingFaq({ ...item });
  };

  const handleSaveFaq = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let updatedItems;
      
      if (editingFaq.id) {
        // Update existing
        updatedItems = faqItems.map(item => 
          item.id === editingFaq.id ? editingFaq : item
        );
      } else {
        // Add new
        updatedItems = [...faqItems, { ...editingFaq, id: Date.now().toString() }];
      }
      
      await axios.put(`${API}/admin/faq`, { items: updatedItems }, { headers });
      setFaqItems(updatedItems);
      setEditingFaq(null);
      toast.success('FAQ enregistrée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updatedItems = faqItems.filter(item => item.id !== id);
      await axios.put(`${API}/admin/faq`, { items: updatedItems }, { headers });
      setFaqItems(updatedItems);
      toast.success('Question supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==================== COLORS FUNCTIONS ====================

  const handleEditColor = (type, color, index) => {
    setColorType(type);
    setEditingColor({
      index,
      color: { ...color }
    });
    setColorDialogOpen(true);
  };

  const handleAddColor = (type) => {
    setColorType(type);
    setEditingColor({
      index: -1,
      color: {
        value: '',
        label: '',
        label_en: '',
        hex: '#000000',
        ral: type === 'ral' ? '' : null,
        is_custom: type === 'ral'
      }
    });
    setColorDialogOpen(true);
  };

  const handleSaveColor = async () => {
    if (!editingColor) return;
    const { index, color } = editingColor;
    
    if (!color.value || !color.label || !color.hex) {
      toast.error('Valeur, nom et code couleur requis');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentColors = colorType === 'base' ? [...baseColors] : [...ralColors];
      
      if (index === -1) {
        currentColors.push(color);
      } else {
        currentColors[index] = color;
      }
      
      await axios.put(`${API}/admin/colors/${colorType}`, currentColors, { headers });
      
      toast.success('Couleur sauvegardée');
      setColorDialogOpen(false);
      setEditingColor(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteColor = async (type, index) => {
    if (!confirm('Supprimer cette couleur ?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentColors = type === 'base' ? [...baseColors] : [...ralColors];
      currentColors.splice(index, 1);
      
      await axios.put(`${API}/admin/colors/${type}`, currentColors, { headers });
      
      toast.success('Couleur supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUploadImage = async (type, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = response.data.url;
      
      if (type === 'product' && editingProduct) {
        setEditingProduct({
          ...editingProduct,
          images: [...(editingProduct.images || []), imageUrl]
        });
      } else if (type === 'accessory' && editingAccessory) {
        setEditingAccessory({
          ...editingAccessory,
          images: [...(editingAccessory.images || []), imageUrl]
        });
      }
      
      toast.success('Photo ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      // Error handled silently
    }
  };

  const handleAddImage = (type) => {
    const url = prompt('URL de l\'image:');
    if (url) {
      if (type === 'product' && editingProduct) {
        setEditingProduct({
          ...editingProduct,
          images: [...(editingProduct.images || []), url]
        });
      } else if (type === 'accessory' && editingAccessory) {
        setEditingAccessory({
          ...editingAccessory,
          images: [...(editingAccessory.images || []), url]
        });
      }
    }
  };

  const handleRemoveImage = (type, index) => {
    if (type === 'product' && editingProduct) {
      const newImages = editingProduct.images.filter((_, i) => i !== index);
      setEditingProduct({ ...editingProduct, images: newImages });
    } else if (type === 'accessory' && editingAccessory) {
      const newImages = editingAccessory.images.filter((_, i) => i !== index);
      setEditingAccessory({ ...editingAccessory, images: newImages });
    }
  };

  // Format config for display
  const formatConfig = (config) => {
    if (!config) return '-';
    const parts = [];
    if (config.pneu) parts.push(config.pneu === 'marathon' ? 'Marathon' : 'Gravel');
    if (config.transmission) {
      const transLabel = config.transmission === '1x10' ? '1×10' : config.transmission === '1x12' ? '1×12' : 'GRX';
      parts.push(transLabel);
    }
    if (config.guidon) parts.push(config.guidon.toUpperCase());
    if (config.taille) parts.push(`T.${config.taille}`);
    if (config.couleur) parts.push(config.couleur);
    if (config.axe) parts.push(config.axe === 'QR' ? 'QR' : 'Trav.');
    // Legacy fields support for old orders
    if (config.type) parts.push(config.type);
    if (config.plateaux) parts.push(`${config.plateaux}×`);
    if (config.vitesses) parts.push(`${config.vitesses}v`);
    if (config.potence) parts.push(`${config.potence}mm`);
    return parts.join(' | ') || '-';
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-4"
        >
          <div className="text-center mb-8">
            <h1 className="font-heading font-black text-3xl uppercase tracking-tight">Administration</h1>
            <p className="font-body text-muted-foreground mt-2">Belleville Machine</p>
          </div>

          <form onSubmit={handleLogin} className="border border-border p-8 space-y-6">
            <div>
              <Label htmlFor="username" className="font-mono text-xs uppercase tracking-widest">
                Identifiant
              </Label>
              <Input
                id="username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="mt-2 rounded-none"
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="mt-2 rounded-none"
                required
                data-testid="admin-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-none font-mono uppercase tracking-widest"
              data-testid="admin-login-btn"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <button
            onClick={handleSetupAdmin}
            className="mt-4 w-full text-center font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="setup-admin-btn"
          >
            Créer le compte admin initial
          </button>

          <button
            onClick={handleRecoverPassword}
            className="mt-2 w-full text-center font-mono text-xs text-[#0047AB] hover:underline transition-colors"
            data-testid="recover-password-btn"
          >
            Mot de passe oublié ?
          </button>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <h1 className="font-heading font-black text-xl uppercase tracking-tight">
            Admin Dashboard
          </h1>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="font-mono text-xs uppercase"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-border p-6">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-muted-foreground" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Visiteurs</span>
              </div>
              <p className="font-mono text-3xl font-bold mt-2">{stats.unique_visitors}</p>
            </div>
            <div className="border border-border p-6">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-muted-foreground" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Commandes</span>
              </div>
              <p className="font-mono text-3xl font-bold mt-2">{stats.total_orders}</p>
            </div>
            <div className="border border-border p-6">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">En attente</span>
              </div>
              <p className="font-mono text-3xl font-bold mt-2">{stats.pending_orders}</p>
            </div>
            <div className="border border-border p-6">
              <div className="flex items-center gap-3">
                <Euro className="w-5 h-5 text-muted-foreground" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">CA</span>
              </div>
              <p className="font-mono text-3xl font-bold mt-2">{formatPrice(stats.total_revenue)}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-secondary rounded-none p-1 flex-wrap">
            <TabsTrigger value="orders" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white">
              Commandes
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white">
              Produits
            </TabsTrigger>
            <TabsTrigger value="options" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white" data-testid="options-tab">
              Options
            </TabsTrigger>
            <TabsTrigger value="customers" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white">
              Clients
            </TabsTrigger>
            <TabsTrigger value="cms" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white">
              Contenu
            </TabsTrigger>
            <TabsTrigger value="stats" className="rounded-none font-mono text-xs uppercase data-[state=active]:bg-white">
              Statistiques
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <h2 className="font-heading font-bold text-xl uppercase">Commandes ({orders.length})</h2>
            
            {orders.length === 0 ? (
              <p className="text-muted-foreground font-mono text-sm">Aucune commande</p>
            ) : (
              <div className="border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary">
                      <TableHead className="font-mono text-xs uppercase">Date</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Client</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Produit</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Configuration</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Total</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Paiement</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Statut</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                        <TableCell className="font-mono text-xs">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-heading font-bold text-sm">{order.customer.prenom} {order.customer.nom}</p>
                            <p className="font-mono text-[10px] text-muted-foreground">{order.customer.email}</p>
                            <p className="font-mono text-[10px] text-muted-foreground">{order.customer.telephone}</p>
                            {order.customer.taille_cycliste && (
                              <p className="font-mono text-[10px] text-[#0047AB]">Taille: {order.customer.taille_cycliste}cm</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {order.items.map((item, idx) => (
                            <div key={idx} className="font-mono text-xs">
                              {item.product_name} ×{item.quantity}
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {order.items.map((item, idx) => (
                            <div key={idx} className="font-mono text-[10px] text-muted-foreground max-w-[200px]">
                              {formatConfig(item.config)}
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="font-mono font-bold">
                          {formatPrice(order.total_amount)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {order.payment_method === 'virement' ? 'Virement' : 
                           order.payment_method === 'carte_acompte' ? 'Carte 50%' : 'Carte 100%'}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-[10px] font-mono uppercase ${
                            order.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.payment_status === 'paid' ? 'Payé' : 'En attente'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditOrder(order)}
                              className="rounded-none font-mono text-[10px] uppercase h-8"
                              data-testid={`edit-order-${order.id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {order.payment_method === 'virement' && order.payment_status !== 'paid' && (
                              <Button
                                size="sm"
                                onClick={() => handleValidateTransfer(order.id)}
                                className="rounded-none font-mono text-[10px] uppercase h-8"
                                data-testid={`validate-${order.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Valider
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteOrder(order.id)}
                              className="rounded-none font-mono text-[10px] uppercase h-8"
                              data-testid={`delete-order-${order.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Order Edit Dialog */}
            <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    Modifier la commande
                  </DialogTitle>
                </DialogHeader>
                {editingOrder && (
                  <div className="space-y-6 mt-4">
                    {/* Customer Info */}
                    <div>
                      <h3 className="font-heading font-bold uppercase text-sm mb-3">Coordonnées client</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="font-mono text-xs uppercase">Prénom</Label>
                          <Input
                            value={editingOrder.customer.prenom}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, prenom: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Nom</Label>
                          <Input
                            value={editingOrder.customer.nom}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, nom: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Email</Label>
                          <Input
                            value={editingOrder.customer.email}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, email: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Téléphone</Label>
                          <Input
                            value={editingOrder.customer.telephone}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, telephone: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="font-mono text-xs uppercase">Adresse</Label>
                          <Input
                            value={editingOrder.customer.adresse}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, adresse: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Code postal</Label>
                          <Input
                            value={editingOrder.customer.code_postal}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, code_postal: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Ville</Label>
                          <Input
                            value={editingOrder.customer.ville}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, ville: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Taille cycliste (cm)</Label>
                          <Input
                            value={editingOrder.customer.taille_cycliste || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              customer: {...editingOrder.customer, taille_cycliste: e.target.value}
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div>
                      <h3 className="font-heading font-bold uppercase text-sm mb-3">Détails commande</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="font-mono text-xs uppercase">Total (€)</Label>
                          <Input
                            type="number"
                            value={editingOrder.total_amount}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              total_amount: e.target.value
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Montant dû (€)</Label>
                          <Input
                            type="number"
                            value={editingOrder.amount_due}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              amount_due: e.target.value
                            })}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Statut paiement</Label>
                          <select
                            value={editingOrder.payment_status}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              payment_status: e.target.value
                            })}
                            className="mt-1 w-full h-10 px-3 border border-input bg-background font-mono text-sm"
                          >
                            <option value="pending">En attente</option>
                            <option value="paid">Payé</option>
                            <option value="partial">Partiel</option>
                            <option value="refunded">Remboursé</option>
                          </select>
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Statut commande</Label>
                          <select
                            value={editingOrder.status}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              status: e.target.value
                            })}
                            className="mt-1 w-full h-10 px-3 border border-input bg-background font-mono text-sm"
                          >
                            <option value="pending">En attente</option>
                            <option value="confirmed">Confirmée</option>
                            <option value="production">En production</option>
                            <option value="shipped">Expédiée</option>
                            <option value="delivered">Livrée</option>
                            <option value="cancelled">Annulée</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Products in order */}
                    <div>
                      <h3 className="font-heading font-bold uppercase text-sm mb-3">Produits</h3>
                      <div className="bg-secondary p-3 space-y-2">
                        {editingOrder.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <div>
                              <span className="font-heading font-bold text-sm">{item.product_name}</span>
                              <span className="font-mono text-xs text-muted-foreground ml-2">×{item.quantity}</span>
                              {item.config && (
                                <p className="font-mono text-[10px] text-muted-foreground">
                                  {formatConfig(item.config)}
                                </p>
                              )}
                            </div>
                            <span className="font-mono font-bold">{formatPrice(item.total_price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <Label className="font-mono text-xs uppercase">Notes internes</Label>
                      <Textarea
                        value={editingOrder.notes || ''}
                        onChange={(e) => setEditingOrder({
                          ...editingOrder,
                          notes: e.target.value
                        })}
                        className="mt-1 rounded-none"
                        rows={3}
                        placeholder="Notes pour cette commande..."
                      />
                    </div>

                    <Button
                      onClick={handleSaveOrder}
                      className="w-full h-12 rounded-none font-mono uppercase"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            {/* Bikes & Kits */}
            <div>
              <h2 className="font-heading font-bold text-xl uppercase mb-4">Vélos & Kits</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <div key={product.id} className="border border-border p-4" data-testid={`product-card-${product.id}`}>
                    <div className="aspect-video bg-secondary mb-4 overflow-hidden">
                      {product.images && product.images[0] ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Image className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-heading font-bold uppercase">{product.name}</h3>
                    <p className="font-mono text-sm font-bold text-[#0047AB]">{formatPrice(product.base_price)}</p>
                    <p className="font-body text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                    <p className="font-mono text-[10px] text-muted-foreground mt-1">
                      {product.images?.length || 0} photo(s)
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4 rounded-none font-mono text-xs uppercase"
                      onClick={() => {
                        setEditingProduct({...product});
                        setProductDialogOpen(true);
                      }}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-2" />
                      Modifier
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Accessories */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading font-bold text-xl uppercase">
                  Accessoires ({accessories.length}/10)
                </h2>
                {accessories.length < 10 && (
                  <Button
                    onClick={() => setNewAccessoryDialogOpen(true)}
                    className="rounded-none font-mono text-xs uppercase"
                    data-testid="add-accessory-btn"
                  >
                    <Plus className="w-3 h-3 mr-2" />
                    Ajouter
                  </Button>
                )}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accessories.map((accessory) => (
                  <div key={accessory.id} className="border border-border p-4" data-testid={`accessory-card-${accessory.id}`}>
                    <div className="aspect-video bg-secondary mb-4 overflow-hidden">
                      {accessory.images && accessory.images[0] ? (
                        <img src={accessory.images[0]} alt={accessory.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Image className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-heading font-bold uppercase">{accessory.name}</h3>
                    <p className="font-mono text-sm font-bold text-[#0047AB]">{formatPrice(accessory.price)}</p>
                    <p className="font-body text-xs text-muted-foreground mt-1 line-clamp-2">{accessory.description}</p>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-none font-mono text-xs uppercase"
                        onClick={() => {
                          setEditingAccessory({...accessory});
                          setAccessoryDialogOpen(true);
                        }}
                        data-testid={`edit-accessory-${accessory.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="rounded-none font-mono text-xs uppercase"
                        onClick={() => handleDeleteAccessory(accessory.id)}
                        data-testid={`delete-accessory-${accessory.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* New Accessory Dialog */}
            <Dialog open={newAccessoryDialogOpen} onOpenChange={setNewAccessoryDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    Nouvel accessoire
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Nom (FR) *</Label>
                    <Input
                      value={newAccessory.name}
                      onChange={(e) => setNewAccessory({...newAccessory, name: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: Béquille double"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Nom (EN)</Label>
                    <Input
                      value={newAccessory.name_en}
                      onChange={(e) => setNewAccessory({...newAccessory, name_en: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: Double kickstand"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Prix (€) *</Label>
                    <Input
                      type="number"
                      value={newAccessory.price}
                      onChange={(e) => setNewAccessory({...newAccessory, price: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: 39"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Description (FR)</Label>
                    <Textarea
                      value={newAccessory.description}
                      onChange={(e) => setNewAccessory({...newAccessory, description: e.target.value})}
                      className="mt-1 rounded-none"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Description (EN)</Label>
                    <Textarea
                      value={newAccessory.description_en}
                      onChange={(e) => setNewAccessory({...newAccessory, description_en: e.target.value})}
                      className="mt-1 rounded-none"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase mb-2 block">Photo</Label>
                    {newAccessory.image && (
                      <div className="relative aspect-video bg-secondary mb-2">
                        <img src={newAccessory.image} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setNewAccessory({...newAccessory, image: ''})}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <label className="w-full">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadNewAccessoryImage(e.target.files[0])}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full rounded-none font-mono text-xs"
                        asChild
                      >
                        <span>
                          <Image className="w-3 h-3 mr-2" />
                          {newAccessory.image ? 'Changer la photo' : 'Choisir une photo'}
                        </span>
                      </Button>
                    </label>
                  </div>
                  <Button
                    onClick={handleCreateAccessory}
                    className="w-full h-12 rounded-none font-mono uppercase"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer l'accessoire
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Product Edit Dialog */}
            <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    Modifier le produit
                  </DialogTitle>
                </DialogHeader>
                {editingProduct && (
                  <div className="space-y-6 mt-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom</Label>
                        <Input
                          value={editingProduct.name}
                          onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Prix (€)</Label>
                        <Input
                          type="number"
                          value={editingProduct.base_price}
                          onChange={(e) => setEditingProduct({...editingProduct, base_price: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                    </div>

                    {/* Section Promotion */}
                    <div className="border border-orange-200 bg-orange-50 p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <input
                          type="checkbox"
                          id="promo_active"
                          checked={editingProduct.promo_active || false}
                          onChange={(e) => setEditingProduct({...editingProduct, promo_active: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="promo_active" className="font-mono text-xs uppercase text-orange-700">
                          🏷️ Activer une promotion
                        </Label>
                      </div>
                      
                      {editingProduct.promo_active && (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label className="font-mono text-xs uppercase text-orange-700">Prix promotionnel (€)</Label>
                              <Input
                                type="number"
                                value={editingProduct.promo_price || ''}
                                onChange={(e) => setEditingProduct({...editingProduct, promo_price: e.target.value})}
                                className="mt-1 rounded-none border-orange-300"
                                placeholder="Ex: 2990"
                              />
                            </div>
                            <div>
                              <Label className="font-mono text-xs uppercase text-orange-700">Fin de promo (date)</Label>
                              <Input
                                type="date"
                                value={editingProduct.promo_end_date || ''}
                                onChange={(e) => setEditingProduct({...editingProduct, promo_end_date: e.target.value})}
                                className="mt-1 rounded-none border-orange-300"
                              />
                            </div>
                          </div>
                          <p className="font-mono text-[10px] text-orange-600">
                            Le prix barré ({editingProduct.base_price}€) et le badge "En promo jusqu'au [date]" s'afficheront sur le site.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Descriptions */}
                    <div>
                      <Label className="font-mono text-xs uppercase">Description (FR)</Label>
                      <Textarea
                        value={editingProduct.description}
                        onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                        className="mt-1 rounded-none"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Description (EN)</Label>
                      <Textarea
                        value={editingProduct.description_en || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, description_en: e.target.value})}
                        className="mt-1 rounded-none"
                        rows={3}
                      />
                    </div>

                    {/* Specs / Caractéristiques Techniques */}
                    <div className="border border-border p-4">
                      <div className="flex justify-between items-center mb-4">
                        <Label className="font-mono text-xs uppercase">Caractéristiques techniques</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddSpec}
                          className="rounded-none font-mono text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                      {editingProduct.specs && Object.keys(editingProduct.specs).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(editingProduct.specs).map(([key, spec]) => (
                            <div key={key} className="grid grid-cols-12 gap-2 items-start">
                              <div className="col-span-3">
                                <Input
                                  placeholder="Label FR"
                                  value={spec.label || ''}
                                  onChange={(e) => handleUpdateSpec(key, 'label', e.target.value)}
                                  className="rounded-none text-xs"
                                />
                              </div>
                              <div className="col-span-3">
                                <Input
                                  placeholder="Label EN"
                                  value={spec.label_en || ''}
                                  onChange={(e) => handleUpdateSpec(key, 'label_en', e.target.value)}
                                  className="rounded-none text-xs"
                                />
                              </div>
                              <div className="col-span-5">
                                <Input
                                  placeholder="Valeur (ex: 22 kg)"
                                  value={spec.value || ''}
                                  onChange={(e) => handleUpdateSpec(key, 'value', e.target.value)}
                                  className="rounded-none text-xs"
                                />
                              </div>
                              <div className="col-span-1">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteSpec(key)}
                                  className="w-full h-9 rounded-none p-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="font-mono text-xs text-muted-foreground">
                          Aucune caractéristique. Cliquez sur "Ajouter" pour en créer.
                        </p>
                      )}
                    </div>

                    {/* Promo / Offre spéciale */}
                    <div className="border border-[#0047AB] p-4 bg-blue-50">
                      <Label className="font-mono text-xs uppercase text-[#0047AB] mb-3 block">
                        Encart promotionnel (optionnel)
                      </Label>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Input
                              placeholder="Titre FR (ex: Offert pour les pré-commandes)"
                              value={editingProduct.promo_title || ''}
                              onChange={(e) => setEditingProduct({...editingProduct, promo_title: e.target.value})}
                              className="rounded-none text-xs"
                            />
                          </div>
                          <div>
                            <Input
                              placeholder="Titre EN"
                              value={editingProduct.promo_title_en || ''}
                              onChange={(e) => setEditingProduct({...editingProduct, promo_title_en: e.target.value})}
                              className="rounded-none text-xs"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Input
                              placeholder="Texte FR (ex: Béquille, housse offerts !)"
                              value={editingProduct.promo_text || ''}
                              onChange={(e) => setEditingProduct({...editingProduct, promo_text: e.target.value})}
                              className="rounded-none text-xs"
                            />
                          </div>
                          <div>
                            <Input
                              placeholder="Texte EN"
                              value={editingProduct.promo_text_en || ''}
                              onChange={(e) => setEditingProduct({...editingProduct, promo_text_en: e.target.value})}
                              className="rounded-none text-xs"
                            />
                          </div>
                        </div>
                        <p className="font-mono text-[10px] text-muted-foreground">
                          Laissez vide pour ne pas afficher d'encart promotionnel
                        </p>
                      </div>
                    </div>

                    {/* Photos */}
                    <div>
                      <Label className="font-mono text-xs uppercase mb-2 block">Photos</Label>
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {editingProduct.images?.map((img, idx) => (
                          <div key={idx} className="relative aspect-video bg-secondary">
                            <img src={img} alt="" className="w-full h-full object-contain" />
                            <button
                              onClick={() => handleRemoveImage('product', idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUploadImage('product', e.target.files[0])}
                            className="hidden"
                          />
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full rounded-none font-mono text-xs"
                            asChild
                          >
                            <span>
                              <Image className="w-3 h-3 mr-2" />
                              Choisir une photo
                            </span>
                          </Button>
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddImage('product')}
                          className="rounded-none font-mono text-xs"
                        >
                          <Plus className="w-3 h-3 mr-2" />
                          URL
                        </Button>
                      </div>
                      <p className="font-mono text-[10px] text-muted-foreground mt-1">
                        Uploadez depuis votre ordinateur ou collez une URL
                      </p>
                    </div>

                    <Button
                      onClick={handleSaveProduct}
                      className="w-full h-12 rounded-none font-mono uppercase"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Accessory Edit Dialog */}
            <Dialog open={accessoryDialogOpen} onOpenChange={setAccessoryDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="font-heading font-bold uppercase">
                    {editingAccessory?.id ? "Modifier l'accessoire" : "Nouvel accessoire"}
                  </DialogTitle>
                </DialogHeader>
                {editingAccessory && (
                  <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom (FR)</Label>
                        <Input
                          value={editingAccessory.name}
                          onChange={(e) => setEditingAccessory({...editingAccessory, name: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom (EN)</Label>
                        <Input
                          value={editingAccessory.name_en || ''}
                          onChange={(e) => setEditingAccessory({...editingAccessory, name_en: e.target.value})}
                          className="mt-1 rounded-none"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Prix (€)</Label>
                      <Input
                        type="number"
                        value={editingAccessory.price}
                        onChange={(e) => setEditingAccessory({...editingAccessory, price: e.target.value})}
                        className="mt-1 rounded-none"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Description (FR)</Label>
                      <Textarea
                        value={editingAccessory.description}
                        onChange={(e) => setEditingAccessory({...editingAccessory, description: e.target.value})}
                        className="mt-1 rounded-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Description (EN)</Label>
                      <Textarea
                        value={editingAccessory.description_en || ''}
                        onChange={(e) => setEditingAccessory({...editingAccessory, description_en: e.target.value})}
                        className="mt-1 rounded-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase mb-2 block">Photos</Label>
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {editingAccessory.images?.map((img, idx) => (
                          <div key={idx} className="relative aspect-square bg-secondary">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                            <button
                              onClick={() => handleRemoveImage('accessory', idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUploadImage('accessory', e.target.files[0])}
                            className="hidden"
                          />
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full rounded-none font-mono text-xs"
                            asChild
                          >
                            <span>
                              <Image className="w-3 h-3 mr-2" />
                              Choisir une photo
                            </span>
                          </Button>
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddImage('accessory')}
                          className="rounded-none font-mono text-xs"
                        >
                          <Plus className="w-3 h-3 mr-2" />
                          URL
                        </Button>
                      </div>
                    </div>
                    <Button
                      onClick={handleSaveAccessory}
                      className="w-full h-12 rounded-none font-mono uppercase sticky bottom-0"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Options Tab - Product Configuration */}
          <TabsContent value="options" className="space-y-8" data-testid="options-tab-content">
            <div>
              <h2 className="font-heading font-bold text-xl uppercase mb-2">Options de configuration</h2>
              <p className="font-mono text-xs text-muted-foreground mb-6">
                Gérez les options disponibles dans le configurateur de vélos (types, plateaux, vitesses, etc.)
              </p>
            </div>

            {/* Configuration Options */}
            {Object.entries(OPTION_CATEGORY_LABELS).map(([category, labels]) => (
              <div key={category} className="border border-border p-6" data-testid={`options-${category}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-heading font-bold uppercase">{labels.fr}</h3>
                  <Button
                    size="sm"
                    onClick={() => handleAddOption(category)}
                    className="rounded-none font-mono text-xs uppercase"
                    data-testid={`add-option-${category}`}
                  >
                    <Plus className="w-3 h-3 mr-2" />
                    Ajouter
                  </Button>
                </div>
                
                {configOptions[category] && configOptions[category].length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {configOptions[category].map((item, idx) => (
                      <div 
                        key={idx} 
                        className="border border-border p-3 bg-white"
                        data-testid={`option-item-${category}-${item.value}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-heading font-bold text-sm uppercase">{item.label}</p>
                            {item.label_en && (
                              <p className="font-mono text-[10px] text-muted-foreground">{item.label_en}</p>
                            )}
                            {item.description && (
                              <p className="font-body text-xs text-muted-foreground mt-1">{item.description}</p>
                            )}
                            {item.price && (
                              <p className="font-mono text-xs text-[#0047AB] mt-1">+{item.price}€</p>
                            )}
                            <p className="font-mono text-[10px] text-gray-400 mt-1">val: {item.value}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditOption(category, item, idx)}
                              className="h-6 w-6 p-0 rounded-none"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteOption(category, idx)}
                              className="h-6 w-6 p-0 rounded-none"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-mono text-xs text-muted-foreground">Aucune option configurée</p>
                )}
              </div>
            ))}

            {/* Colors Section */}
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-xl uppercase">Couleurs</h2>
              
              {/* Base Colors */}
              <div className="border border-border p-6" data-testid="base-colors-section">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-heading font-bold uppercase">Couleurs de base</h3>
                    <p className="font-mono text-[10px] text-muted-foreground">Couleurs gratuites</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAddColor('base')}
                    className="rounded-none font-mono text-xs uppercase"
                    data-testid="add-base-color"
                  >
                    <Plus className="w-3 h-3 mr-2" />
                    Ajouter
                  </Button>
                </div>
                
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {baseColors.map((color, idx) => (
                    <div 
                      key={idx} 
                      className="border border-border p-3 text-center"
                      data-testid={`base-color-${color.value}`}
                    >
                      <div 
                        className="w-10 h-10 mx-auto rounded-full border-2 border-border mb-2"
                        style={{ backgroundColor: color.hex }}
                      />
                      <p className="font-mono text-[10px] uppercase">{color.label}</p>
                      <p className="font-mono text-[8px] text-muted-foreground">{color.hex}</p>
                      <div className="flex gap-1 mt-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditColor('base', color, idx)}
                          className="h-5 w-5 p-0 rounded-none"
                        >
                          <Pencil className="w-2 h-2" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteColor('base', idx)}
                          className="h-5 w-5 p-0 rounded-none"
                        >
                          <Trash2 className="w-2 h-2" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RAL Colors */}
              <div className="border border-border p-6" data-testid="ral-colors-section">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-heading font-bold uppercase">Couleurs RAL personnalisées</h3>
                    <p className="font-mono text-[10px] text-muted-foreground">+50€ par vélo</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAddColor('ral')}
                    className="rounded-none font-mono text-xs uppercase"
                    data-testid="add-ral-color"
                  >
                    <Plus className="w-3 h-3 mr-2" />
                    Ajouter
                  </Button>
                </div>
                
                <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {ralColors.map((color, idx) => (
                    <div 
                      key={idx} 
                      className="border border-border p-2 text-center"
                      data-testid={`ral-color-${color.ral}`}
                    >
                      <div 
                        className="w-8 h-8 mx-auto rounded-full border border-border mb-1"
                        style={{ backgroundColor: color.hex }}
                      />
                      <p className="font-mono text-[9px] leading-tight">{color.label}</p>
                      <p className="font-mono text-[8px] text-muted-foreground">RAL {color.ral}</p>
                      <div className="flex gap-1 mt-1 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditColor('ral', color, idx)}
                          className="h-5 w-5 p-0 rounded-none"
                        >
                          <Pencil className="w-2 h-2" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteColor('ral', idx)}
                          className="h-5 w-5 p-0 rounded-none"
                        >
                          <Trash2 className="w-2 h-2" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Option Edit Dialog */}
            <Dialog open={optionDialogOpen} onOpenChange={setOptionDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    {editingOption?.index === -1 ? 'Nouvelle option' : 'Modifier option'}
                  </DialogTitle>
                </DialogHeader>
                {editingOption && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="font-mono text-xs uppercase">Valeur (code unique) *</Label>
                      <Input
                        value={editingOption.item.value}
                        onChange={(e) => setEditingOption({
                          ...editingOption,
                          item: { ...editingOption.item, value: e.target.value }
                        })}
                        className="mt-1 rounded-none"
                        placeholder="Ex: street, gravel, 12..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom FR *</Label>
                        <Input
                          value={editingOption.item.label}
                          onChange={(e) => setEditingOption({
                            ...editingOption,
                            item: { ...editingOption.item, label: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Street"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom EN</Label>
                        <Input
                          value={editingOption.item.label_en || ''}
                          onChange={(e) => setEditingOption({
                            ...editingOption,
                            item: { ...editingOption.item, label_en: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Street"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-xs uppercase">Description FR</Label>
                        <Input
                          value={editingOption.item.description || ''}
                          onChange={(e) => setEditingOption({
                            ...editingOption,
                            item: { ...editingOption.item, description: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Pneus Schwalbe..."
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Description EN</Label>
                        <Input
                          value={editingOption.item.description_en || ''}
                          onChange={(e) => setEditingOption({
                            ...editingOption,
                            item: { ...editingOption.item, description_en: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Schwalbe tires..."
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Supplément prix (€)</Label>
                      <Input
                        type="number"
                        value={editingOption.item.price || ''}
                        onChange={(e) => setEditingOption({
                          ...editingOption,
                          item: { ...editingOption.item, price: e.target.value ? parseFloat(e.target.value) : null }
                        })}
                        className="mt-1 rounded-none"
                        placeholder="Ex: 50"
                      />
                      <p className="font-mono text-[10px] text-muted-foreground mt-1">
                        Laissez vide si pas de supplément
                      </p>
                    </div>
                    <Button
                      onClick={handleSaveOption}
                      className="w-full h-12 rounded-none font-mono uppercase"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Color Edit Dialog */}
            <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    {editingColor?.index === -1 ? 'Nouvelle couleur' : 'Modifier couleur'}
                  </DialogTitle>
                </DialogHeader>
                {editingColor && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="font-mono text-xs uppercase">Valeur (code unique) *</Label>
                      <Input
                        value={editingColor.color.value}
                        onChange={(e) => setEditingColor({
                          ...editingColor,
                          color: { ...editingColor.color, value: e.target.value }
                        })}
                        className="mt-1 rounded-none"
                        placeholder={colorType === 'ral' ? 'Ex: RAL 3000' : 'Ex: bleu'}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom FR *</Label>
                        <Input
                          value={editingColor.color.label}
                          onChange={(e) => setEditingColor({
                            ...editingColor,
                            color: { ...editingColor.color, label: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Bleu"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Nom EN</Label>
                        <Input
                          value={editingColor.color.label_en || ''}
                          onChange={(e) => setEditingColor({
                            ...editingColor,
                            color: { ...editingColor.color, label_en: e.target.value }
                          })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: Blue"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-xs uppercase">Code couleur HEX *</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="color"
                            value={editingColor.color.hex}
                            onChange={(e) => setEditingColor({
                              ...editingColor,
                              color: { ...editingColor.color, hex: e.target.value }
                            })}
                            className="w-12 h-10 p-1 rounded-none"
                          />
                          <Input
                            value={editingColor.color.hex}
                            onChange={(e) => setEditingColor({
                              ...editingColor,
                              color: { ...editingColor.color, hex: e.target.value }
                            })}
                            className="flex-1 rounded-none font-mono"
                            placeholder="#1E4C87"
                          />
                        </div>
                      </div>
                      {colorType === 'ral' && (
                        <div>
                          <Label className="font-mono text-xs uppercase">Code RAL</Label>
                          <Input
                            value={editingColor.color.ral || ''}
                            onChange={(e) => setEditingColor({
                              ...editingColor,
                              color: { ...editingColor.color, ral: e.target.value }
                            })}
                            className="mt-1 rounded-none"
                            placeholder="Ex: 3000"
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleSaveColor}
                      className="w-full h-12 rounded-none font-mono uppercase"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading font-bold text-xl uppercase">Clients ({customers.length})</h2>
              <div className="flex gap-2">
                <Button
                  onClick={handleExportExcel}
                  variant="outline"
                  className="rounded-none font-mono text-xs uppercase"
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-none font-mono text-xs uppercase" data-testid="add-customer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau client
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading font-bold uppercase">Nouveau client</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCustomer} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-mono text-xs uppercase">Prénom</Label>
                          <Input
                            value={newCustomer.prenom}
                            onChange={(e) => setNewCustomer({ ...newCustomer, prenom: e.target.value })}
                            className="mt-1 rounded-none"
                            required
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Nom</Label>
                          <Input
                            value={newCustomer.nom}
                            onChange={(e) => setNewCustomer({ ...newCustomer, nom: e.target.value })}
                            className="mt-1 rounded-none"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Email</Label>
                        <Input
                          type="email"
                          value={newCustomer.email}
                          onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                          className="mt-1 rounded-none"
                          required
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Téléphone</Label>
                        <Input
                          value={newCustomer.telephone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, telephone: e.target.value })}
                          className="mt-1 rounded-none"
                          required
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Adresse</Label>
                        <Input
                          value={newCustomer.adresse}
                          onChange={(e) => setNewCustomer({ ...newCustomer, adresse: e.target.value })}
                          className="mt-1 rounded-none"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-mono text-xs uppercase">Code postal</Label>
                          <Input
                            value={newCustomer.code_postal}
                            onChange={(e) => setNewCustomer({ ...newCustomer, code_postal: e.target.value })}
                            className="mt-1 rounded-none"
                            required
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs uppercase">Ville</Label>
                          <Input
                            value={newCustomer.ville}
                            onChange={(e) => setNewCustomer({ ...newCustomer, ville: e.target.value })}
                            className="mt-1 rounded-none"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Taille cycliste (cm)</Label>
                        <Input
                          value={newCustomer.taille_cycliste}
                          onChange={(e) => setNewCustomer({ ...newCustomer, taille_cycliste: e.target.value })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: 175"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Détails commande</Label>
                        <Textarea
                          value={newCustomer.order_details}
                          onChange={(e) => setNewCustomer({ ...newCustomer, order_details: e.target.value })}
                          className="mt-1 rounded-none"
                          placeholder="Ex: FOLGO Street, Taille M, Bleu..."
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase">Notes</Label>
                        <Textarea
                          value={newCustomer.notes}
                          onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                          className="mt-1 rounded-none"
                          placeholder="Notes internes..."
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-12 rounded-none font-mono uppercase"
                        data-testid="create-customer-btn"
                      >
                        Créer le client
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {customers.length === 0 ? (
              <p className="text-muted-foreground font-mono text-sm">Aucun client</p>
            ) : (
              <div className="space-y-4">
                {customers.map((customer, idx) => (
                  <div key={idx} className="border border-border p-6" data-testid={`customer-${idx}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-heading font-bold uppercase">
                          {customer.customer.prenom} {customer.customer.nom}
                        </span>
                        <span className={`ml-2 px-2 py-1 text-[10px] font-mono uppercase ${
                          customer.source === 'manual' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                        }`}>
                          {customer.source === 'manual' ? 'Manuel' : 'En ligne'}
                        </span>
                        <p className="font-mono text-xs text-muted-foreground mt-1">
                          {customer.customer.email} • {customer.customer.telephone}
                        </p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {customer.customer.adresse}, {customer.customer.code_postal} {customer.customer.ville}
                        </p>
                        {customer.customer.taille_cycliste && (
                          <p className="font-mono text-xs text-[#0047AB] mt-1">
                            Taille: {customer.customer.taille_cycliste}cm
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-lg font-bold">{formatPrice(customer.total_spent)}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {customer.orders?.length || 0} commande(s)
                        </p>
                      </div>
                    </div>
                    {customer.order_details && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Commande</p>
                        <p className="font-body text-sm">{customer.order_details}</p>
                      </div>
                    )}
                    {customer.notes && (
                      <div className="mt-2">
                        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</p>
                        <p className="font-body text-sm text-muted-foreground">{customer.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CMS Tab - Content Management */}
          <TabsContent value="cms" className="space-y-8">
            {/* Bank Details Section */}
            <div className="border border-border p-6">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">
                Coordonnées bancaires (virement)
              </h2>
              <p className="font-mono text-xs text-muted-foreground mb-4">
                Ces informations seront affichées aux clients qui choisissent le paiement par virement
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs uppercase">IBAN</Label>
                  <Input
                    value={bankDetails.iban}
                    onChange={(e) => setBankDetails({...bankDetails, iban: e.target.value})}
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    className="mt-1 rounded-none font-mono"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase">Nom du titulaire</Label>
                  <Input
                    value={bankDetails.account_name}
                    onChange={(e) => setBankDetails({...bankDetails, account_name: e.target.value})}
                    placeholder="BELLEVILLE MACHINE / FOLGO SAS"
                    className="mt-1 rounded-none"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="font-mono text-xs uppercase">Lien vers le RIB (PDF)</Label>
                <Input
                  value={bankDetails.rib_url}
                  onChange={(e) => setBankDetails({...bankDetails, rib_url: e.target.value})}
                  placeholder="https://..."
                  className="mt-1 rounded-none font-mono text-xs"
                />
                <p className="font-mono text-[10px] text-muted-foreground mt-1">
                  URL du fichier PDF du RIB que les clients pourront télécharger
                </p>
              </div>
              <Button
                onClick={handleSaveBankDetails}
                className="mt-4 rounded-none font-mono text-xs uppercase"
              >
                <Save className="w-3 h-3 mr-2" />
                Enregistrer
              </Button>
            </div>

            {/* About Page Video Section */}
            <div className="border border-border p-6">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">
                Vidéo Page À Propos
              </h2>
              <p className="font-mono text-xs text-muted-foreground mb-4">
                Ajoutez une vidéo YouTube, Vimeo ou un lien direct vers un fichier vidéo
              </p>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre (Français)</Label>
                    <Input
                      value={aboutContent.video_title_fr || ''}
                      onChange={(e) => setAboutContent({...aboutContent, video_title_fr: e.target.value})}
                      placeholder="Découvrez Belleville Machine"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre (Anglais)</Label>
                    <Input
                      value={aboutContent.video_title_en || ''}
                      onChange={(e) => setAboutContent({...aboutContent, video_title_en: e.target.value})}
                      placeholder="Discover Belleville Machine"
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase">Type de vidéo</Label>
                  <select
                    value={aboutContent.video_type || 'youtube'}
                    onChange={(e) => setAboutContent({...aboutContent, video_type: e.target.value})}
                    className="w-full mt-1 p-2 border border-border rounded-none font-mono text-sm bg-background"
                  >
                    <option value="youtube">YouTube</option>
                    <option value="vimeo">Vimeo</option>
                    <option value="direct">Lien direct (MP4, etc.)</option>
                  </select>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase">URL de la vidéo</Label>
                  <Input
                    value={aboutContent.video_url || ''}
                    onChange={(e) => setAboutContent({...aboutContent, video_url: e.target.value})}
                    placeholder={aboutContent.video_type === 'youtube' 
                      ? "https://www.youtube.com/watch?v=..." 
                      : aboutContent.video_type === 'vimeo'
                      ? "https://vimeo.com/..."
                      : "https://...video.mp4"}
                    className="mt-1 rounded-none font-mono text-xs"
                  />
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">
                    {aboutContent.video_type === 'youtube' && "Collez l'URL complète de la vidéo YouTube"}
                    {aboutContent.video_type === 'vimeo' && "Collez l'URL complète de la vidéo Vimeo"}
                    {aboutContent.video_type === 'direct' && "Collez l'URL directe du fichier vidéo (MP4, WebM...)"}
                  </p>
                </div>
                {aboutContent.video_url && (
                  <div className="p-2 bg-secondary text-xs font-mono">
                    Aperçu: La vidéo sera affichée sur la page À propos
                  </div>
                )}
              </div>
              <Button
                onClick={handleSaveAboutContent}
                className="mt-4 rounded-none font-mono text-xs uppercase"
              >
                <Save className="w-3 h-3 mr-2" />
                Enregistrer la vidéo
              </Button>
            </div>

            {/* Home Page Content Section */}
            <div className="border border-border p-6">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">
                Textes Page d'Accueil
              </h2>
              <p className="font-mono text-xs text-muted-foreground mb-4">
                Modifiez les textes affichés sur la page d'accueil du site
              </p>
              
              {/* Hero Section */}
              <div className="space-y-4 mb-6">
                <h3 className="font-heading font-bold text-sm uppercase border-b border-border pb-2">Section Hero</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Sous-titre (FR)</Label>
                    <Input
                      value={homeContent.hero_subtitle_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_subtitle_fr: e.target.value})}
                      placeholder="Vélos cargo français"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Sous-titre (EN)</Label>
                    <Input
                      value={homeContent.hero_subtitle_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_subtitle_en: e.target.value})}
                      placeholder="French cargo bikes"
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre ligne 1 (FR)</Label>
                    <Input
                      value={homeContent.hero_title1_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_title1_fr: e.target.value})}
                      placeholder="BELLEVILLE"
                      className="mt-1 rounded-none font-heading font-bold"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre ligne 1 (EN)</Label>
                    <Input
                      value={homeContent.hero_title1_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_title1_en: e.target.value})}
                      placeholder="BELLEVILLE"
                      className="mt-1 rounded-none font-heading font-bold"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre ligne 2 - bleu (FR)</Label>
                    <Input
                      value={homeContent.hero_title2_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_title2_fr: e.target.value})}
                      placeholder="MACHINE"
                      className="mt-1 rounded-none font-heading font-bold text-[#0047AB]"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre ligne 2 - bleu (EN)</Label>
                    <Input
                      value={homeContent.hero_title2_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_title2_en: e.target.value})}
                      placeholder="MACHINE"
                      className="mt-1 rounded-none font-heading font-bold text-[#0047AB]"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Description (FR)</Label>
                    <textarea
                      value={homeContent.hero_description_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_description_fr: e.target.value})}
                      placeholder="Des vélos cargo conçus et assemblés en France..."
                      rows={3}
                      className="mt-1 w-full p-2 border border-border rounded-none font-body text-sm resize-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Description (EN)</Label>
                    <textarea
                      value={homeContent.hero_description_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_description_en: e.target.value})}
                      placeholder="Cargo bikes designed and assembled in France..."
                      rows={3}
                      className="mt-1 w-full p-2 border border-border rounded-none font-body text-sm resize-none"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Bouton principal (FR)</Label>
                    <Input
                      value={homeContent.hero_cta_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_cta_fr: e.target.value})}
                      placeholder="Découvrir"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Bouton principal (EN)</Label>
                    <Input
                      value={homeContent.hero_cta_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_cta_en: e.target.value})}
                      placeholder="Discover"
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Bouton contact (FR)</Label>
                    <Input
                      value={homeContent.hero_cta_contact_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_cta_contact_fr: e.target.value})}
                      placeholder="Nous contacter"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Bouton contact (EN)</Label>
                    <Input
                      value={homeContent.hero_cta_contact_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, hero_cta_contact_en: e.target.value})}
                      placeholder="Contact us"
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-xs uppercase">Image Hero (URL)</Label>
                  <Input
                    value={homeContent.hero_image_url || ''}
                    onChange={(e) => setHomeContent({...homeContent, hero_image_url: e.target.value})}
                    placeholder="https://... (laisser vide pour l'image par défaut)"
                    className="mt-1 rounded-none font-mono text-xs"
                  />
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">
                    Uploadez une image via l'onglet Produits et collez l'URL ici
                  </p>
                </div>
              </div>

              {/* Products Section */}
              <div className="space-y-4 mb-6">
                <h3 className="font-heading font-bold text-sm uppercase border-b border-border pb-2">Section Produits</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Sous-titre (FR)</Label>
                    <Input
                      value={homeContent.products_subtitle_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, products_subtitle_fr: e.target.value})}
                      placeholder="Notre gamme"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Sous-titre (EN)</Label>
                    <Input
                      value={homeContent.products_subtitle_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, products_subtitle_en: e.target.value})}
                      placeholder="Our range"
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre (FR)</Label>
                    <Input
                      value={homeContent.products_title_fr || ''}
                      onChange={(e) => setHomeContent({...homeContent, products_title_fr: e.target.value})}
                      placeholder="Nos vélos"
                      className="mt-1 rounded-none font-heading font-bold"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Titre (EN)</Label>
                    <Input
                      value={homeContent.products_title_en || ''}
                      onChange={(e) => setHomeContent({...homeContent, products_title_en: e.target.value})}
                      placeholder="Our bikes"
                      className="mt-1 rounded-none font-heading font-bold"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveHomeContent}
                className="rounded-none font-mono text-xs uppercase"
              >
                <Save className="w-3 h-3 mr-2" />
                Enregistrer les textes
              </Button>
            </div>

            {/* FAQ Section */}
            <div className="border border-border p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading font-bold text-xl uppercase">
                  FAQ ({faqItems.length} {faqItems.length <= 1 ? 'question' : 'questions'})
                </h2>
                <Button
                  onClick={handleAddFaq}
                  className="rounded-none font-mono text-xs uppercase"
                >
                  <Plus className="w-3 h-3 mr-2" />
                  Ajouter une question
                </Button>
              </div>

              {editingFaq && (
                <div className="mb-6 p-4 bg-secondary border border-border space-y-4">
                  <h3 className="font-heading font-bold">
                    {editingFaq.id ? 'Modifier la question' : 'Nouvelle question'}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs uppercase">Question (FR)</Label>
                      <Input
                        value={editingFaq.question_fr}
                        onChange={(e) => setEditingFaq({...editingFaq, question_fr: e.target.value})}
                        placeholder="Question en français"
                        className="mt-1 rounded-none"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Question (EN)</Label>
                      <Input
                        value={editingFaq.question_en}
                        onChange={(e) => setEditingFaq({...editingFaq, question_en: e.target.value})}
                        placeholder="Question in English"
                        className="mt-1 rounded-none"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs uppercase">Réponse (FR)</Label>
                      <textarea
                        value={editingFaq.answer_fr}
                        onChange={(e) => setEditingFaq({...editingFaq, answer_fr: e.target.value})}
                        placeholder="Réponse en français"
                        rows={4}
                        className="mt-1 w-full p-2 border border-border rounded-none font-mono text-sm resize-none"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase">Réponse (EN)</Label>
                      <textarea
                        value={editingFaq.answer_en}
                        onChange={(e) => setEditingFaq({...editingFaq, answer_en: e.target.value})}
                        placeholder="Answer in English"
                        rows={4}
                        className="mt-1 w-full p-2 border border-border rounded-none font-mono text-sm resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveFaq} className="rounded-none font-mono text-xs">
                      <Save className="w-3 h-3 mr-2" />
                      Enregistrer
                    </Button>
                    <Button onClick={() => setEditingFaq(null)} variant="outline" className="rounded-none font-mono text-xs">
                      Annuler
                    </Button>
                  </div>
                </div>
              )}

              {faqItems.length === 0 ? (
                <p className="text-muted-foreground font-mono text-sm">Aucune question FAQ pour le moment.</p>
              ) : (
                <div className="space-y-3">
                  {faqItems.map((item, index) => (
                    <div key={item.id || index} className="p-4 border border-border flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-heading font-bold text-sm">{item.question_fr}</p>
                        <p className="font-mono text-xs text-muted-foreground mt-1 line-clamp-2">{item.answer_fr}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditFaq(item)}
                          className="rounded-none"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteFaq(item.id)}
                          className="rounded-none"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Partner Stores Section */}
            <div className="border border-border p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading font-bold text-xl uppercase">
                  Points d'essai ({partnerStores.length})
                </h2>
                <Button
                  onClick={() => setStoreDialogOpen(true)}
                  className="rounded-none font-mono text-xs uppercase"
                >
                  <Plus className="w-3 h-3 mr-2" />
                  Ajouter
                </Button>
              </div>
              
              {partnerStores.length === 0 ? (
                <p className="text-muted-foreground font-mono text-sm">Aucun point d'essai configuré</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {partnerStores.map((store) => (
                    <div key={store.id} className="border border-border p-4">
                      {editingStore && editingStore.id === store.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editingStore.name}
                            onChange={(e) => setEditingStore({...editingStore, name: e.target.value})}
                            placeholder="Nom"
                            className="rounded-none"
                          />
                          <Input
                            value={editingStore.address}
                            onChange={(e) => setEditingStore({...editingStore, address: e.target.value})}
                            placeholder="Adresse"
                            className="rounded-none"
                          />
                          <Input
                            value={editingStore.city}
                            onChange={(e) => setEditingStore({...editingStore, city: e.target.value})}
                            placeholder="Ville"
                            className="rounded-none"
                          />
                          <Input
                            value={editingStore.phone || ''}
                            onChange={(e) => setEditingStore({...editingStore, phone: e.target.value})}
                            placeholder="Téléphone"
                            className="rounded-none"
                          />
                          <Input
                            value={editingStore.website || ''}
                            onChange={(e) => setEditingStore({...editingStore, website: e.target.value})}
                            placeholder="Site web"
                            className="rounded-none"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleUpdateStore} className="flex-1 rounded-none font-mono text-xs">
                              <Save className="w-3 h-3 mr-1" />
                              Sauver
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingStore(null)} className="rounded-none">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-heading font-bold uppercase">{store.name}</h3>
                          <p className="font-mono text-xs text-muted-foreground mt-1">
                            {store.address && `${store.address}, `}{store.city}
                          </p>
                          {store.phone && <p className="font-mono text-xs mt-1">{store.phone}</p>}
                          {store.website && <p className="font-mono text-xs mt-1 truncate">{store.website}</p>}
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingStore({...store})}
                              className="flex-1 rounded-none font-mono text-[10px]"
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteStore(store.id)}
                              className="rounded-none font-mono text-[10px]"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* New Store Dialog */}
            <Dialog open={storeDialogOpen} onOpenChange={setStoreDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    Nouveau point d'essai
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="font-mono text-xs uppercase">Nom du magasin *</Label>
                    <Input
                      value={newStore.name}
                      onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: Vélo & Co"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Adresse</Label>
                    <Input
                      value={newStore.address}
                      onChange={(e) => setNewStore({...newStore, address: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: 15 rue du Commerce"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Ville *</Label>
                    <Input
                      value={newStore.city}
                      onChange={(e) => setNewStore({...newStore, city: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: Paris"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Téléphone</Label>
                    <Input
                      value={newStore.phone}
                      onChange={(e) => setNewStore({...newStore, phone: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: 01 23 45 67 89"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase">Site web</Label>
                    <Input
                      value={newStore.website}
                      onChange={(e) => setNewStore({...newStore, website: e.target.value})}
                      className="mt-1 rounded-none"
                      placeholder="Ex: https://www.example.com"
                    />
                  </div>
                  <Button
                    onClick={handleCreateStore}
                    className="w-full h-12 rounded-none font-mono uppercase"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le point d'essai
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Info about CMS */}
            <div className="border border-border p-6 bg-secondary/50">
              <h3 className="font-heading font-bold uppercase mb-2">Gestion du contenu</h3>
              <p className="font-mono text-xs text-muted-foreground">
                Pour modifier les textes des produits, allez dans l'onglet "Produits" et cliquez sur "Modifier".
                <br />
                Pour modifier les accessoires, descendez dans la section "Accessoires" de l'onglet "Produits".
              </p>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-6">
            {stats && (
              <>
                {/* Chart - Views by Date */}
                <div className="border border-border p-6">
                  <h3 className="font-heading font-bold uppercase mb-6">Visites par jour</h3>
                  {stats.views_by_date && stats.views_by_date.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.views_by_date}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 10, fontFamily: 'monospace' }}
                            tickFormatter={(value) => value.slice(5)}
                          />
                          <YAxis tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                          <Tooltip 
                            contentStyle={{ 
                              fontFamily: 'monospace', 
                              fontSize: 11,
                              border: '1px solid #000',
                              borderRadius: 0
                            }}
                          />
                          <Legend 
                            wrapperStyle={{ fontFamily: 'monospace', fontSize: 10 }}
                          />
                          <Bar dataKey="home" name="Accueil" fill="#0047AB" />
                          <Bar dataKey="product-folgo" name="FOLGO" fill="#1E4C87" />
                          <Bar dataKey="product-cargo" name="CARGO" fill="#2874B2" />
                          <Bar dataKey="kit-cadre" name="Kit Cadre" fill="#57A638" />
                          <Bar dataKey="accessoires" name="Accessoires" fill="#E75B12" />
                          <Bar dataKey="contact" name="Contact" fill="#A03472" />
                          <Bar dataKey="checkout" name="Checkout" fill="#383E42" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-muted-foreground font-mono text-sm">Pas encore de données</p>
                  )}
                </div>

                {/* Charts - Monthly and Yearly */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Monthly Chart */}
                  <div className="border border-border p-6">
                    <h3 className="font-heading font-bold uppercase mb-6">Visites par mois</h3>
                    {stats.views_by_month && stats.views_by_month.length > 0 ? (
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={stats.views_by_month}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 10, fontFamily: 'monospace' }}
                              tickFormatter={(value) => {
                                const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                                const monthNum = parseInt(value.slice(5, 7)) - 1;
                                return months[monthNum] || value.slice(5);
                              }}
                            />
                            <YAxis tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                            <Tooltip 
                              contentStyle={{ 
                                fontFamily: 'monospace', 
                                fontSize: 11,
                                border: '1px solid #000',
                                borderRadius: 0
                              }}
                              labelFormatter={(value) => {
                                const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                                const monthNum = parseInt(value.slice(5, 7)) - 1;
                                return `${months[monthNum]} ${value.slice(0, 4)}`;
                              }}
                            />
                            <Bar dataKey="visits" name="Visites" fill="#0047AB" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-muted-foreground font-mono text-sm">Pas encore de données</p>
                    )}
                  </div>

                  {/* Yearly Chart */}
                  <div className="border border-border p-6">
                    <h3 className="font-heading font-bold uppercase mb-6">Visites par année</h3>
                    {stats.views_by_year && stats.views_by_year.length > 0 ? (
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={stats.views_by_year}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                            <XAxis 
                              dataKey="year" 
                              tick={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 'bold' }}
                            />
                            <YAxis tick={{ fontSize: 10, fontFamily: 'monospace' }} />
                            <Tooltip 
                              contentStyle={{ 
                                fontFamily: 'monospace', 
                                fontSize: 11,
                                border: '1px solid #000',
                                borderRadius: 0
                              }}
                              labelFormatter={(value) => `Année ${value}`}
                            />
                            <Bar dataKey="visits" name="Visites" fill="#57A638" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-muted-foreground font-mono text-sm">Pas encore de données</p>
                    )}
                  </div>
                </div>

                {/* Page views summary */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border border-border p-6">
                    <h3 className="font-heading font-bold uppercase mb-4">Total visites</h3>
                    <p className="font-mono text-4xl font-bold">{stats.total_page_views}</p>
                    <p className="font-mono text-xs text-muted-foreground mt-1">
                      {stats.unique_visitors} visiteurs uniques
                    </p>
                  </div>

                  <div className="border border-border p-6">
                    <h3 className="font-heading font-bold uppercase mb-4">Par page</h3>
                    {stats.page_views_by_page && stats.page_views_by_page.length > 0 ? (
                      <div className="space-y-2">
                        {stats.page_views_by_page.slice(0, 8).map((page, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="font-mono text-xs truncate max-w-[150px]">{page._id}</span>
                            <div className="flex items-center gap-2">
                              <div 
                                className="h-2 bg-[#0047AB]" 
                                style={{ width: `${Math.min(page.count * 10, 100)}px` }}
                              />
                              <span className="font-mono text-xs font-bold w-8 text-right">{page.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground font-mono text-sm">Aucune donnée</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
